function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function cleanValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => cleanValue(item))
      .filter((item) => item !== undefined);
  }

  if (value && typeof value === "object") {
    return Object.keys(value).reduce((carry, key) => {
      const nextValue = cleanValue(value[key]);
      if (nextValue !== undefined) {
        carry[key] = nextValue;
      }
      return carry;
    }, {});
  }

  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  return value;
}

function stringValue(value) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  return String(value);
}

function getPrice(item = {}) {
  return toNumber(item.discount || item.price || item.origin_price || item.total);
}

function getParentNames(item = {}) {
  if (!Array.isArray(item.parents)) {
    return [];
  }

  return item.parents
    .map((parent) => parent?.name || parent?.text || parent?.title)
    .filter(Boolean)
    .slice(0, 5);
}

function getCategoryItem(category = {}, product = null, quantity = 1, extra = {}) {
  const parentNames = getParentNames(category);
  const price = product ? getPrice(product) : getPrice(category);

  return cleanValue({
    item_id: stringValue(category.no || category.id || product?.no || product?.id),
    item_name: category.name || product?.name,
    item_variant: product?.name,
    item_variant_id: stringValue(product?.no || product?.id),
    item_category: parentNames[0],
    item_category2: parentNames[1],
    item_category3: parentNames[2],
    price,
    quantity: toNumber(quantity) || 1,
    ...extra,
  });
}

function getLineItem(item = {}, extra = {}) {
  return cleanValue({
    item_id: stringValue(
      item.category_no ||
        item.category_id ||
        item.product_no ||
        item.product_id ||
        item.no ||
        item.id,
    ),
    item_name: item.category_name || item.name,
    item_variant: item.product_name || item.variant_name || item.variant,
    item_variant_id: stringValue(item.product_no || item.product_id || item.no),
    price: getPrice(item),
    quantity: toNumber(item.quantity) || 1,
    has_options: Boolean(item.options && Object.keys(item.options).length),
    ...extra,
  });
}

function getItems(items = []) {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item, index) => getLineItem(item, { index })).filter(Boolean);
}

function getOrderCoupon(order = {}) {
  return order.coupon?.code || order.coupon_code || order.coupon_id;
}

function getPaymentMethod(payment = {}) {
  return payment.method || payment.name || payment.bank_name;
}

export default ({ app, store, route }, inject) => {
  const getLocaleContext = () => {
    const locale = app.i18n?.locale;
    const locales = store.getters["app/locales"] || [];
    const langs = store.getters["app/lang"] || [];
    const currentLocale = locales.find(
      (item) => String(item.code).toLowerCase() === String(locale).toLowerCase(),
    );
    const currentLang = langs.find(
      (item) => String(item.locale).toLowerCase() === String(locale).toLowerCase(),
    );

    return {
      locale,
      region: locale,
      region_name: currentLocale?.name,
      currency: currentLang?.currency || process.env.CURRECNY || process.env.CURRENCY,
    };
  };

  const getPlatform = () => {
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";

    if (store.getters["app/isAlipayHK"]) return "alipayhk_miniapp";
    if (store.getters["app/inMpayMiniApp"]) return "mpay_miniapp";
    if (/GamemasterAppWebView/i.test(userAgent)) return "app_webview";
    if (/MicroMessenger/i.test(userAgent)) return "wechat";
    return "web";
  };

  const getContext = () => {
    const currentRoute = app.router?.currentRoute || route;
    const info = store.getters["app/info"] || {};

    return cleanValue({
      app_name: info.name || process.env.APP_NAME,
      page_path: currentRoute?.fullPath,
      page_name: currentRoute?.name,
      platform: getPlatform(),
      user_type: app.$auth?.loggedIn ? "member" : "guest",
      ...getLocaleContext(),
    });
  };

  const push = (event, params = {}, options = {}) => {
    if (typeof window === "undefined") {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    if (options.clearEcommerce) {
      window.dataLayer.push({ ecommerce: null });
    }

    window.dataLayer.push(
      cleanValue({
        event,
        ...getContext(),
        ...params,
      }),
    );
  };

  const ecommerce = (event, ecommerceParams = {}, params = {}) => {
    push(
      event,
      {
        ...params,
        ecommerce: {
          currency: getLocaleContext().currency,
          ...ecommerceParams,
        },
      },
      { clearEcommerce: true },
    );
  };

  const tracking = {
    event: push,
    ecommerce,
    categoryItem: getCategoryItem,
    lineItem: getLineItem,
    lineItems: getItems,
    viewItemList(items, list = {}) {
      const listId = list.id || "all";
      const listName = list.name || "全部商品";
      const visibleItems = Array.isArray(items) ? items.slice(0, 24) : [];

      ecommerce(
        "view_item_list",
        {
          item_list_id: listId,
          item_list_name: listName,
          items: visibleItems.map((item, index) =>
            getCategoryItem(item, null, 1, {
              index,
              item_list_id: listId,
              item_list_name: listName,
            }),
          ),
        },
        {
          results_count: Array.isArray(items) ? items.length : 0,
          search_term: list.searchTerm,
        },
      );
    },
    selectItem(category, list = {}) {
      const listId = list.id || "all";
      const listName = list.name || "全部商品";

      ecommerce("select_item", {
        item_list_id: listId,
        item_list_name: listName,
        items: [
          getCategoryItem(category, null, 1, {
            index: list.index,
            item_list_id: listId,
            item_list_name: listName,
          }),
        ],
      });
    },
    viewItem(category, product) {
      ecommerce("view_item", {
        value: getPrice(product || category),
        items: [getCategoryItem(category, product)],
      });
    },
    addToCart(category, product, quantity = 1) {
      const item = getCategoryItem(category, product, quantity);

      ecommerce("add_to_cart", {
        value: (toNumber(item.price) || 0) * (toNumber(quantity) || 1),
        items: [item],
      });
    },
    viewCart(cart = {}) {
      ecommerce("view_cart", {
        value: toNumber(cart.value),
        items: getItems(cart.items),
      });
    },
    beginCheckout(cart = {}) {
      ecommerce("begin_checkout", {
        value: toNumber(cart.value),
        coupon: cart.coupon,
        items: getItems(cart.items),
      });
    },
    addPaymentInfo(order = {}, payment = {}) {
      ecommerce("add_payment_info", {
        value: toNumber(payment.total || order.total),
        payment_type: getPaymentMethod(payment),
        items: getItems(order.items),
      });
    },
    purchase(order = {}) {
      ecommerce("purchase", {
        transaction_id: stringValue(order.no || order.order_id || order.id),
        value: toNumber(order.total),
        tax: toNumber(order.tax),
        shipping: toNumber(order.shipping),
        coupon: getOrderCoupon(order),
        payment_type: getPaymentMethod(order.payment),
        items: getItems(order.items),
      });
    },
  };

  inject("track", tracking);
};
